// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/FeedMemoryCard.test.tsx
import { CaretDown, Database } from "@phosphor-icons/react";
import { Button, Text, TextField } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";
import {
  type ProjectFeedEvent,
  type ProjectMemoryPayload,
} from "../../../services/askPunttProjectFeed.types";
import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type FeedMemoryCardVariant =
  | "open"
  | "edit"
  | "changeScope"
  | "editedOpen";

export type FeedMemoryScopeOption = {
  id: string;
  label: string;
  description?: string;
};

/**
 * Feed confirmation for a remembered instruction. Collapsible AgentSteps-style
 * header (Figma FeedMemoryCard 49613:961) — transparent fill; open shell
 * strokes gray-3 to match the header hover fill. Variants add
 * Edit / Change Scope / Save via callbacks only; there is no persist API here.
 */
export function FeedMemoryCard({
  event,
  variant = "open",
  open: openProp,
  onOpenChange,
  onEdit,
  onSave,
  onChangeScope,
  onCancel,
  source,
  scope,
}: {
  event: ProjectFeedEvent;
  variant?: FeedMemoryCardVariant;
  /** When set, controls expand/collapse like EnterpriseImpact. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEdit?: () => void;
  onSave?: (instruction: string) => void;
  onChangeScope?: () => void;
  onCancel?: () => void;
  source?: string;
  scope?: string;
  /** @deprecated Figma changeScope is a text field; kept so old callers type-check. */
  scopes?: FeedMemoryScopeOption[];
  selectedScopeIds?: string[];
  onToggleScope?: (id: string) => void;
}) {
  const { t } = useTranslation("ui");
  const payload = (event.payload ?? {}) as ProjectMemoryPayload;
  // why: `instruction_committed` events (christo commitInstruction, rembrandt commit_project_instruction) persist `text` + `scope`, not `instruction`.
  const committedText =
    typeof event.payload?.text === "string" ? event.payload.text : undefined;
  const savedScope = event.payload?.scope;
  const instruction = payload.instruction ?? committedText ?? event.body ?? "";
  const [draft, setDraft] = useState(instruction);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp != null;
  const expanded = isControlled ? openProp : uncontrolledOpen;
  // why: warhol's chat atom carries no scope, and its save_memory writes enterprise or personal memory, never project -- only claim a scope the payload names.
  const heading =
    savedScope === "project"
      ? t("feedMemoryCard.savedToProjectMemory")
      : savedScope === "enterprise"
        ? t("feedMemoryCard.savedToEnterpriseMemory")
        : t("feedMemoryCard.savedToMemory");
  const showActions = Boolean(onEdit || onChangeScope);
  const editing = variant === "edit";
  const scoping = variant === "changeScope";
  const edited = variant === "editedOpen";
  // why: edit/changeScope must stay visible while drafting — don't collapse mid-edit.
  const bodyVisible = expanded || editing || scoping;

  const setExpanded = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <section
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.feedMemoryCard)}
      // why: same muted accordion shell as AgentSteps / SearchProgress — rounded-xl clips the padded hover fill.
      className={cx(
        "overflow-hidden bg-transparent",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          open: bodyVisible,
        }),
      )}
      data-testid="project-feed-memory-saved"
      data-variant={variant}
      aria-label={heading}
    >
      <button
        type="button"
        data-testid="feed-memory-toggle"
        aria-expanded={bodyVisible}
        aria-label={
          bodyVisible
            ? t("feedMemoryCard.collapseAriaLabel")
            : t("feedMemoryCard.expandAriaLabel")
        }
        onClick={() => {
          if (editing || scoping) return;
          setExpanded(!expanded);
        }}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        <Database
          size={16}
          className="shrink-0 text-puntt-neutral-gray-12"
          aria-hidden="true"
        />
        <Text size="1" className="text-puntt-neutral-gray-12">
          {heading}
        </Text>
        <CaretDown
          size={14}
          aria-hidden="true"
          className={`ml-auto shrink-0 text-puntt-neutral-gray-12 transition-transform duration-200 ${bodyVisible ? "rotate-0" : "-rotate-90"}`}
        />
      </button>

      {bodyVisible ? (
        <div className={STATUS_PANEL_ITEMS_MUTED_CLASS}>
          {editing ? (
            <div className="flex flex-col gap-1">
              <TextField.Root
                id={`feed-memory-instruction-${event._id}`}
                data-testid="feed-memory-instruction-input"
                size="2"
                aria-label={t("feedMemoryCard.instructionLabel")}
                placeholder={t("feedMemoryCard.instructionLabel")}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  data-testid="feed-memory-save"
                  size="1"
                  variant="outline"
                  className="inline-flex h-6 items-center"
                  disabled={draft.trim().length === 0}
                  onClick={() => onSave?.(draft.trim())}
                >
                  {t("feedMemoryCard.save")}
                </Button>
                {onCancel ? (
                  <Button
                    type="button"
                    data-testid="feed-memory-cancel"
                    size="1"
                    variant="ghost"
                    className="inline-flex h-6 items-center"
                    onClick={onCancel}
                  >
                    {t("feedMemoryCard.cancel")}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : scoping ? (
            <div className="flex flex-col gap-1">
              <TextField.Root
                data-testid="feed-memory-scope-input"
                size="2"
                aria-label={t("feedMemoryCard.scopeHeading")}
                placeholder={t("feedMemoryCard.scopeHeading")}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  data-testid="feed-memory-save"
                  size="1"
                  variant="outline"
                  className="inline-flex h-6 items-center"
                  onClick={() => onSave?.(draft.trim() || instruction)}
                >
                  {t("feedMemoryCard.save")}
                </Button>
                {onCancel ? (
                  <Button
                    type="button"
                    data-testid="feed-memory-cancel"
                    size="1"
                    variant="ghost"
                    className="inline-flex h-6 items-center"
                    onClick={onCancel}
                  >
                    {t("feedMemoryCard.cancel")}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {instruction ? (
                <div className="flex flex-col gap-1 bg-transparent">
                  <Text
                    as="p"
                    size="1"
                    weight={edited ? "medium" : "regular"}
                    className="bg-transparent text-puntt-neutral-gray-12"
                    data-testid="project-feed-memory-instruction"
                  >
                    {instruction}
                  </Text>
                  {source ? (
                    <Text
                      as="p"
                      size="1"
                      className="bg-transparent text-puntt-neutral-gray-11"
                      data-testid="project-feed-memory-source"
                    >
                      {source}
                    </Text>
                  ) : null}
                  {scope ? (
                    <Text
                      as="p"
                      size="1"
                      className="bg-transparent text-puntt-neutral-gray-11"
                      data-testid="project-feed-memory-scope"
                    >
                      {scope}
                    </Text>
                  ) : null}
                </div>
              ) : null}
              {showActions ? (
                <div className="flex items-center gap-1">
                  {onEdit ? (
                    <Button
                      type="button"
                      data-testid="feed-memory-edit"
                      size="1"
                      variant="outline"
                      className="inline-flex h-6 items-center"
                      onClick={onEdit}
                    >
                      {t("feedMemoryCard.edit")}
                    </Button>
                  ) : null}
                  {onChangeScope ? (
                    <Button
                      type="button"
                      data-testid="feed-memory-change-scope"
                      size="1"
                      variant="ghost"
                      className="inline-flex h-6 items-center"
                      onClick={onChangeScope}
                    >
                      {t("feedMemoryCard.changeScope")}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
